import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useRef, useState } from 'react';
import { generatePath } from 'react-router-dom';

import { useTypedParams } from '@sorare/routing';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import {
  ANY_SPORT_MY_CLUB_SLUG_CARDS,
  FOOTBALL_MY_CLUB_SLUG,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { isType } from '@sorare/core/src/lib/gql';

import { DeckEditorDialog } from '@sorare/marketplace/src/components/decks/DeckEditorDialog';

import { Aside } from '@sorare/gameplay/src/components/profile/Aside';
import { ClubLayout } from '@sorare/gameplay/src/components/profile/ClubLayout';
import { CurrentFixture } from '@sorare/gameplay/src/components/profile/CurrentFixture';
import {
  Gallery,
  MAX_CARDS_TO_DISPLAY,
} from '@sorare/gameplay/src/components/profile/Gallery';
import { ManagerIDHeader } from '@sorare/gameplay/src/components/profile/ManagerIDHeader';
import { RecentPurchases } from '@sorare/gameplay/src/components/profile/RecentPurchases';
import type { CampaignWinner } from '@sorare/gameplay/src/components/profile/RepresentedBy';
import { RepresentedBy } from '@sorare/gameplay/src/components/profile/RepresentedBy';
import { WatchlistsBlock } from '@sorare/gameplay/src/components/profile/WatchlistsBlock';
import {
  Grid,
  Main,
  NewGrid,
} from '@sorare/gameplay/src/components/profile/ui';

import type {
  FootballManagerProfileQuery,
  FootballManagerProfileQueryVariables,
} from './__generated__/page.graphql';
import type { PageParams } from './__generated__/routeParams';

const FOOTBALL_MANAGER_PROFILE_QUERY = gql`
  query FootballManagerProfileQuery(
    $user: String!
    $sport: Sport!
    $firstCardsToDisplay: Int!
  ) {
    so5 {
      so5Fixture(sport: $sport, type: LIVE, eventType: CLASSIC) {
        slug
        ...CurrentFixture_so5Fixture
      }
    }
    user(slug: $user) {
      slug
      trades(sport: [$sport], first: 20) {
        nodes {
          ...RecentPurchases_tokenOperationTradeInterface
        }
      }
      cards(
        sport: FOOTBALL
        first: $firstCardsToDisplay
        rarities: [unique, super_rare, rare, limited, common]
        sorts: [RARITIES]
      ) {
        nodes {
          slug
          ...Gallery_anyCard
        }
      }
      footballUserProfile {
        id
        clubName
        background {
          id
          pictureUrl
        }
        socialPictureFingerprint
        highlightedDeck {
          slug
          name
          ...Gallery_deck
        }
        ...ManagerIDHeader_userSportProfile
      }
      ...ManagerIDHeader_user
      ...Gallery_publicUserInfoInterface
      ...CurrentFixture_user
      ...WatchlistsBlock_user
    }
  }
  ${ManagerIDHeader.fragments.user}
  ${ManagerIDHeader.fragments.userSportProfile}
  ${Gallery.fragments.deck}
  ${Gallery.fragments.anyCard}
  ${Gallery.fragments.publicUserInfoInterface}
  ${CurrentFixture.fragments.so5Fixture}
  ${CurrentFixture.fragments.user}
  ${RecentPurchases.fragments.tokenOperationTradeInterface}
  ${WatchlistsBlock.fragments.user}
` as TypedDocumentNode<
  FootballManagerProfileQuery,
  FootballManagerProfileQueryVariables
>;

const FootballManagerProfilePage = () => {
  const { generateSportContextPath } = useSportContext();
  const { slug } = useTypedParams<PageParams>();
  const {
    flags: { campaignsWinners = [] },
  } = useFeatureFlags<{
    campaignsWinners: CampaignWinner[];
  }>();

  const [editingListSlug, setEditingListSlug] = useState<string | null>();
  const { up: isLaptopAndAbove } = useScreenSize('laptop');
  const galleryScrollRef = useRef<HTMLDivElement>(null);
  const { data, loading } = useQuery(FOOTBALL_MANAGER_PROFILE_QUERY, {
    variables: {
      sport: Sport.FOOTBALL,
      user: slug,
      firstCardsToDisplay: MAX_CARDS_TO_DISPLAY,
    },
    skip: !slug,
  });

  if (loading || !data) {
    return <LoadingIndicator fullHeight />;
  }

  const { user, so5 } = data;
  const { so5Fixture } = so5;
  const { cards, footballUserProfile, trades } = user;

  const shareProps = {
    managerProfilePath: generatePath(FOOTBALL_MY_CLUB_SLUG, { slug }),
    managerProfileSocialPictures:
      data?.user.footballUserProfile?.managerProfileSocialPictureUrls,
    galleryPath: generateSportContextPath(ANY_SPORT_MY_CLUB_SLUG_CARDS, {
      params: { slug },
    }),
    gallerySocialPictures:
      data?.user.footballUserProfile?.gallerySocialPictureUrls,
  };

  const campaignWinner = campaignsWinners.find(
    ({ represents }) => represents === data?.user.id
  );

  const filteredRecentPurchases = (trades.nodes || []).filter(
    transaction =>
      (isType(transaction, 'TokenAuction') ||
        isType(transaction, 'TokenPrimaryOffer')) &&
      !!transaction.anyCards?.length
  );

  const blocks = {
    header: (
      <ManagerIDHeader
        user={data.user}
        sport={Sport.FOOTBALL}
        userSportProfile={footballUserProfile}
        {...shareProps}
      />
    ),
    currentFixture: (
      <CurrentFixture
        so5Fixture={so5Fixture}
        sport={Sport.FOOTBALL}
        user={user}
      />
    ),
    recentPurchases: (
      <RecentPurchases
        userSlug={user.slug}
        transactions={filteredRecentPurchases.slice(0, 5)}
      />
    ),
    watchlists: <WatchlistsBlock user={user} />,
    gallery: (
      <>
        <Gallery
          scrollRef={galleryScrollRef}
          sport={Sport.FOOTBALL}
          cards={cards.nodes}
          highlightedDeck={footballUserProfile?.highlightedDeck}
          onAddDeck={() => setEditingListSlug(null)}
          onEditDeck={deckSlug => setEditingListSlug(deckSlug)}
          user={data.user}
        />
        {editingListSlug !== undefined && (
          <DeckEditorDialog
            sport={Sport.FOOTBALL}
            slug={editingListSlug || ''}
            defaultName="Hall Of Fame"
            onClose={() => setEditingListSlug(undefined)}
          />
        )}
      </>
    ),
    representedBy: campaignWinner && (
      <RepresentedBy campaignWinner={campaignWinner} />
    ),
  };

  const backgroundUrl = footballUserProfile?.background?.pictureUrl;

  if (!isLaptopAndAbove) {
    return (
      <ClubLayout home bannerUrl={backgroundUrl}>
        <Grid>
          {blocks.header}
          {blocks.currentFixture}
          {blocks.recentPurchases}
          {blocks.watchlists}
          {blocks.representedBy}
          {blocks.gallery}
        </Grid>
      </ClubLayout>
    );
  }
  return (
    <ClubLayout home bannerUrl={backgroundUrl}>
      <NewGrid>
        <Main>
          {blocks.header}
          {blocks.gallery}
        </Main>
        <Aside>
          {blocks.currentFixture}
          {blocks.recentPurchases}
          {blocks.watchlists}
          {blocks.representedBy}
        </Aside>
      </NewGrid>
    </ClubLayout>
  );
};

export default FootballManagerProfilePage;
